import { api, ApiError } from "./http";
import { apiBase } from "./config";
import { realtime } from "./realtime";
import type { Frame } from "./types";
import type { JoinInfo } from "./sfu";
import { CalendarError, SPURT_PROVIDER, createEvent } from "./calendarApi";
import type { Guest, Repeat } from "./calendarApi";

/* Spurt meetings: the REST contract in docs/MEETINGS_CONTRACT.md. Times are epoch milliseconds. */

export type MeetingKind = "instant" | "scheduled" | "recurring" | "webinar" | "channel";
export type MeetingStatus = "scheduled" | "live" | "ended" | "canceled";
export type MeetingRole = "organizer" | "co-organizer" | "presenter" | "attendee";
export type Rsvp = "none" | "accepted" | "tentative" | "declined";

export interface Invitee {
  type: "user" | "guest" | "team" | "conversation";
  userId?: string;
  email?: string;
  name?: string;
  groupId?: string;
  rsvp?: Rsvp;
  optional?: boolean;
}

export interface MeetingSettings {
  allowUnmute: boolean;
  screenShare: "everyone" | "presenters" | "none";
  chat: "everyone" | "hosts" | "off";
  camerasOff: boolean;
  autoRecord: boolean;
  qaModeration: boolean;
  anonymousQa: boolean;
  lobbyMessage?: string;
}

export interface AgendaItem {
  title: string;
  minutes?: number;
  presenter?: string;
  notes?: string;
}

export interface MeetingView {
  id: string;
  seriesId?: string;
  origStartMs?: number;
  title: string;
  description?: string;
  kind: MeetingKind;
  status: MeetingStatus;
  organizerId: string;
  organizerName?: string;
  coOrganizers?: string[];
  presenters?: string[];
  chatConvId?: string;
  startMs: number;
  endMs: number;
  timeZone?: string;
  rrule?: string;
  invitees?: Invitee[];
  agenda?: AgendaItem[];
  code?: string;
  joinUrl?: string;
  passcode?: string;
  hasPasscode: boolean;
  access: "invited" | "org" | "anyone";
  lobbyBypass: "everyone" | "org" | "invited" | "organizers";
  aiEnabled: boolean;
  e2ee?: boolean;
  settings: MeetingSettings;
  recordingId?: string;
  myRole: MeetingRole;
  myRsvp?: Rsvp;
  live: { locked: boolean; present: number; waiting: number; handsRaised: number; recording: boolean; startedAtMs?: number };
  endedAtMs?: number;
  seq: number;
  recurring?: boolean;
}

export interface Caps {
  role: "host" | "presenter" | "attendee" | "viewer";
  pubAudio: boolean;
  pubVideo: boolean;
  pubScreen: boolean;
  subscribe: boolean;
}

export interface JoinOut {
  status: "joined" | "lobby";
  meetingId: string;
  title: string;
  role: MeetingRole;
  participantId: string;
  join?: JoinInfo & { room?: string; expiresIn?: number; region?: string };
  caps?: Caps;
  lobbyMessage?: string;
  chatConvId?: string;
  recording?: { active: boolean; by?: string; startedMs?: number; consentRequired: boolean };
  guestToken?: string;
  e2ee?: boolean;
}

export interface LobbyEntry {
  id: string;
  name: string;
  guest?: boolean;
  sinceMs: number;
  email?: string;
}

export interface RosterEntry {
  userId: string;
  name: string;
  role: string;
  guest?: boolean;
  room?: string;
  joinedMs: number;
  handRaised?: boolean;
  audio?: boolean;
  video?: boolean;
  muted?: boolean;
  pinned?: boolean;
  spotlight?: boolean;
}

export interface RosterOut {
  items: RosterEntry[];
  waiting?: number;
  layout?: string;
  locked: boolean;
}

export interface RecordingRef {
  id: string;
  status: "recording" | "finished" | "failed";
  startedMs: number;
  endedMs?: number;
  by?: string;
  error?: string;
}

export interface CreateMeetingInput {
  title?: string;
  description?: string;
  startMs?: number;
  endMs?: number;
  timeZone?: string;
  rrule?: string;
  instant?: boolean;
  invitees?: { userId?: string; email?: string; name?: string; optional?: boolean }[];
  agenda?: AgendaItem[];
  access?: MeetingView["access"];
  lobbyBypass?: MeetingView["lobbyBypass"];
  requirePasscode?: boolean;
  aiEnabled?: boolean;
  settings?: Partial<MeetingSettings>;
  convId?: string;
}

const M = "/api/v1/meetings";
const e = encodeURIComponent;
const post = <T = void>(path: string, body?: unknown): Promise<T> => api<T>(path, { method: "POST", ...(body !== undefined ? { body } : {}) });

/* ---------------- meetings ---------------- */
export const createMeeting = (input: CreateMeetingInput): Promise<MeetingView> => post<MeetingView>(M, input);
export const listMeetings = async (fromMs: number, toMs: number): Promise<MeetingView[]> => (await api<{ items: MeetingView[] }>(M, { query: { from: fromMs, to: toMs } })).items ?? [];
export const getMeeting = (id: string): Promise<MeetingView> => api<MeetingView>(`${M}/${e(id)}`);
export const patchMeeting = (id: string, patch: Record<string, unknown>, scope: "single" | "series" = "single"): Promise<MeetingView> =>
  api<MeetingView>(`${M}/${e(id)}`, { method: "PATCH", body: patch, query: { scope } });
export const cancelMeeting = (id: string, scope: "single" | "series" = "single"): Promise<void> => api<void>(`${M}/${e(id)}`, { method: "DELETE", query: { scope } });
export const meetingLink = (id: string): Promise<{ joinUrl: string; code: string; hasPasscode: boolean; passcode?: string }> => api(`${M}/${e(id)}/link`);
export const addInvitees = (id: string, invitees: NonNullable<CreateMeetingInput["invitees"]>): Promise<MeetingView> => post<MeetingView>(`${M}/${e(id)}/invitees`, { invitees });
export const removeInvitee = (id: string, key: string): Promise<MeetingView> => api<MeetingView>(`${M}/${e(id)}/invitees/${e(key)}`, { method: "DELETE" });
export const rsvp = (id: string, answer: "accepted" | "tentative" | "declined"): Promise<MeetingView> => post<MeetingView>(`${M}/${e(id)}/rsvp`, { answer });

/* ---------------- joining (signed in) ---------------- */
export const joinMeeting = (id: string, body: { passcode?: string; name?: string } = {}): Promise<JoinOut> => post<JoinOut>(`${M}/${e(id)}/join`, body);
export const joinByCode = (code: string, body: { passcode?: string; name?: string } = {}): Promise<JoinOut> => post<JoinOut>(`${M}/join-by-code`, { code, ...body });
export const leaveMeeting = (id: string): Promise<void> => post(`${M}/${e(id)}/leave`);

/** The media server can answer iceServers: null when no TURN is set up; the browser throws on null. */
export const cleanJoin = (j: JoinOut): JoinOut => (j.join ? { ...j, join: { ...j.join, iceServers: j.join.iceServers ?? [] } } : j);

/* ---------------- joining as a guest (no sign-in): plain fetch, the guest token is the credential ---------------- */
export class PublicError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "PublicError";
  }
}

async function pub<T>(path: string, init: { method: "GET" | "POST"; body?: unknown; guestToken?: string }): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (init.body !== undefined) headers["Content-Type"] = "application/json";
  if (init.guestToken) headers["X-Guest-Token"] = init.guestToken;
  const req: RequestInit = { method: init.method, headers, credentials: "omit" };
  if (init.body !== undefined) req.body = JSON.stringify(init.body);
  let res: Response;
  try {
    res = await fetch(apiBase() + path, req);
  } catch {
    throw new PublicError(0, "network", "Can't reach the server. Check your connection.");
  }
  if (!res.ok) {
    let code = "http_" + res.status;
    let message = res.statusText || "Request failed";
    try {
      const j = (await res.json()) as { error?: string; message?: string };
      if (j.error) code = j.error;
      if (j.message) message = j.message;
    } catch {
      /* not JSON */
    }
    throw new PublicError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const cleanCode = (c: string): string => c.replace(/[\s-]/g, "").toUpperCase();
export const publicInfo = (code: string): Promise<{ valid: boolean; requiresPasscode: boolean }> => pub(`/api/v1/public/meetings/${e(cleanCode(code))}`, { method: "GET" });
/** First call: send name (and email, passcode). Later calls (lobby polling): send the guestToken you were given, body {}. */
export const publicJoin = (code: string, body: { name?: string; email?: string; passcode?: string }, guestToken?: string): Promise<JoinOut> =>
  pub<JoinOut>(`/api/v1/public/meetings/${e(cleanCode(code))}/join`, { method: "POST", body, ...(guestToken ? { guestToken } : {}) }).then(cleanJoin);
export const publicLeave = (code: string, guestToken: string): Promise<void> => pub<void>(`/api/v1/public/meetings/${e(cleanCode(code))}/leave`, { method: "POST", guestToken });

/** The code in a join link ("https://host/join/ABCD-EFGH-JKMN" or a bare code), or "" when there is none. */
export const codeFromUrl = (url: string): string => {
  const m = /\/join\/([A-Za-z0-9-]{12,16})/.exec(url) ?? /^([A-Za-z0-9-]{12,16})$/.exec(url.trim());
  return m?.[1] ? cleanCode(m[1]) : "";
};

/* ---------------- lobby and host controls ---------------- */
export const lobby = async (id: string): Promise<LobbyEntry[]> => (await api<{ items: LobbyEntry[] }>(`${M}/${e(id)}/lobby`)).items ?? [];
export const admit = (id: string, who: string[] | "all"): Promise<{ count: number }> => post(`${M}/${e(id)}/lobby/admit`, who === "all" ? { all: true } : { ids: who });
export const deny = (id: string, who: string[] | "all"): Promise<{ count: number }> => post(`${M}/${e(id)}/lobby/deny`, who === "all" ? { all: true } : { ids: who });
export const muteUser = (id: string, userId: string, kind: "audio" | "video" = "audio"): Promise<void> => post(`${M}/${e(id)}/mute`, { userId, kind });
export const muteAll = (id: string, allowUnmute?: boolean): Promise<void> => post(`${M}/${e(id)}/mute-all`, allowUnmute === undefined ? {} : { allowUnmute });
export const camerasOff = (id: string, off: boolean): Promise<void> => post(`${M}/${e(id)}/cameras`, { off });
export const removeUser = (id: string, userId: string): Promise<void> => post(`${M}/${e(id)}/remove`, { userId });
export const lockMeeting = (id: string, locked: boolean): Promise<void> => post(`${M}/${e(id)}/lock`, { locked });
export const patchSettings = (id: string, s: Partial<MeetingSettings> & { enforce?: boolean }): Promise<MeetingView> => api<MeetingView>(`${M}/${e(id)}/settings`, { method: "PATCH", body: s });
export const setRole = (id: string, userId: string, role: "presenter" | "attendee" | "co-organizer"): Promise<{ userId: string; role: string; rejoinRequired: boolean }> => post(`${M}/${e(id)}/role`, { userId, role });
export const transferHost = (id: string, userId: string): Promise<void> => post(`${M}/${e(id)}/transfer-host`, { userId });
export const endMeeting = (id: string): Promise<void> => post(`${M}/${e(id)}/end`);
export const raiseHand = (id: string, raised: boolean, userId?: string): Promise<{ raised: boolean; position?: number; count: number }> => post(`${M}/${e(id)}/hand`, { raised, ...(userId ? { userId } : {}) });
export const react = (id: string, emoji: string): Promise<void> => post(`${M}/${e(id)}/reactions`, { emoji });
export const roster = (id: string): Promise<RosterOut> => api<RosterOut>(`${M}/${e(id)}/roster`);

/* ---------------- recording ---------------- */
export const startRecording = (id: string): Promise<void> => post(`${M}/${e(id)}/recording/start`);
export const stopRecording = (id: string): Promise<void> => post(`${M}/${e(id)}/recording/stop`);
export const recordingConsent = (id: string, accept: boolean): Promise<void> => post(`${M}/${e(id)}/recording/consent`, { accept });
export const recordings = async (id: string): Promise<RecordingRef[]> => (await api<{ items: RecordingRef[] }>(`${M}/${e(id)}/recordings`)).items ?? [];
/** A time-limited address the browser can play (needs the recording to be processed first). */
export const recordingUrl = (recId: string, kind: "audio" | "video" = "video"): Promise<{ url: string; expiresInSec: number }> => api(`/api/v1/recordings/${e(recId)}/url`, { query: { kind } });

/* ---------------- captions ---------------- */
export interface CaptionSettings {
  aiEnabled: boolean;
  captionsEnabled: boolean;
  captionLang?: string;
  translationEnabled: boolean;
  canManage: boolean;
}
export const captionSettings = (id: string): Promise<CaptionSettings> => api<CaptionSettings>(`/api/v1/captions/${e(id)}/settings`);
export const setCaptionSettings = (id: string, s: Partial<Pick<CaptionSettings, "aiEnabled" | "captionsEnabled" | "captionLang" | "translationEnabled">>): Promise<CaptionSettings> =>
  api<CaptionSettings>(`/api/v1/captions/${e(id)}/settings`, { method: "PUT", body: s });
export interface CaptionSegment {
  id: string;
  meetingId: string;
  speaker: string;
  speakerName?: string;
  text: string;
  delta?: string;
  final: boolean;
  lang?: string;
  startMs: number;
  endMs: number;
}

/* ---------------- minutes ---------------- */
export interface MinutesSummary {
  id: string;
  meetingId: string;
  title: string;
  status: "draft" | "sent";
  genState: "ready" | "generating" | "failed";
  startMs: number;
  durationMs: number;
  actionItems: number;
  openActionItems: number;
  summary: string;
}
export interface ActionItem {
  id: string;
  description: string;
  ownerName?: string;
  dueMs?: number;
  priority: "high" | "medium" | "low";
  status: "open" | "in_progress" | "done" | "canceled";
}
export interface Minutes {
  id: string;
  meetingId: string;
  title: string;
  status: "draft" | "sent";
  summary: string;
  topics: { title: string; points: string[] }[];
  decisions: string[];
  actionItems: ActionItem[];
  participants: { userId: string; name: string; totalMs: number }[];
  openQuestions: string[];
  nextSteps: string[];
  genState: "ready" | "generating" | "failed";
  genError?: string;
  limitations?: string[];
}
export interface MinutesOut extends Minutes {
  can: { manage: boolean; admin: boolean };
}
export const listMinutes = async (query: { meetingId?: string; limit?: number } = {}): Promise<MinutesSummary[]> => (await api<{ items: MinutesSummary[] }>("/api/v1/minutes", { query })).items ?? [];
export const getMinutes = (id: string): Promise<MinutesOut> => api<MinutesOut>(`/api/v1/minutes/${e(id)}`);
export const generateMinutes = (meetingId: string, regenerate = false): Promise<{ minutes: MinutesOut; generated: boolean; existing: boolean }> => post("/api/v1/minutes/generate", { meetingId, regenerate });
export const sendMinutes = (id: string, opts: { includeInvitees?: boolean; postToChat?: boolean } = {}): Promise<unknown> => post(`/api/v1/minutes/${e(id)}/send`, opts);
export const updateActionItem = (minutesId: string, itemId: string, patch: { status?: ActionItem["status"] }): Promise<unknown> =>
  api(`/api/v1/minutes/${e(minutesId)}/actions/${e(itemId)}`, { method: "PATCH", body: patch });

/* ---------------- realtime frames ("meeting.*", "caption.segment", "minutes.sent") ---------------- */
export type MeetingFrame = { t: string; d: Record<string, unknown> & { meetingId?: string } };
/** Subscribe to meeting frames on the chat socket. Pass a meetingId to see only that meeting's frames. */
export const onMeetingFrame = (h: (f: MeetingFrame) => void, meetingId?: string): (() => void) =>
  realtime.onFrame((f: Frame) => {
    if (!(f.t.startsWith("meeting.") || f.t === "caption.segment" || f.t === "minutes.sent")) return;
    const d = (f.d && typeof f.d === "object" ? f.d : {}) as MeetingFrame["d"];
    if (meetingId && d.meetingId !== meetingId) return;
    h({ t: f.t, d });
  });

/* ---------------- glue for the calendar ---------------- */
export interface SpurtMeetingArgs {
  title: string;
  startAt: string; // ISO
  endAt: string; // ISO
  guests: Guest[];
  agenda: string;
  notes: string;
  repeat: Repeat;
}
export interface SpurtMeetingLink {
  id: string;
  code: string;
  url: string;
}

const RRULE: Record<string, string> = { daily: "FREQ=DAILY", weekdays: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR", weekly: "FREQ=WEEKLY", monthly: "FREQ=MONTHLY", yearly: "FREQ=YEARLY" };

type Inv = NonNullable<CreateMeetingInput["invitees"]>[number];
const inviteesOf = (guests: Guest[]): Inv[] =>
  guests.flatMap((g): Inv[] => {
    if (g.userId) return [{ userId: g.userId, ...(g.name ? { name: g.name } : {}) }];
    if (g.email) return [{ email: g.email, ...(g.name ? { name: g.name } : {}) }];
    return []; // a name with no account and no e-mail can't be invited to join; it is only listed on the event
  });

const agendaOf = (text: string): AgendaItem[] =>
  text
    .split("\n")
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim())
    .filter(Boolean)
    .slice(0, 50)
    .map((title) => ({ title: title.slice(0, 200) }));

const bodyFor = (a: SpurtMeetingArgs): CreateMeetingInput => {
  const body: CreateMeetingInput = {
    title: a.title,
    startMs: Date.parse(a.startAt),
    endMs: Date.parse(a.endAt),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Lagos",
    invitees: inviteesOf(a.guests),
    access: "invited", // only the people invited can join; anyone else is refused
    lobbyBypass: "invited",
    aiEnabled: true,
  };
  if (a.notes) body.description = a.notes;
  const agenda = agendaOf(a.agenda);
  if (agenda.length) body.agenda = agenda;
  const rr = RRULE[a.repeat];
  if (rr) body.rrule = rr;
  return body;
};

/** Where Spurt's own link to the calendar service stands: "active" = Spurt adds the event to the calendar itself. */
export async function calendarPush(): Promise<"active" | "off"> {
  try {
    const s = await api<{ enabled: boolean; prefs?: { enabled: boolean } }>("/api/v1/calsvc/status");
    return s.enabled && s.prefs?.enabled !== false ? "active" : "off";
  } catch {
    return "off";
  }
}

/**
 * Create the meeting. When Spurt's calendar link is active the server puts the event on the person's calendar by itself
 * (one event, kept in step with the meeting); otherwise this adds the event to the calendar service with the join link.
 */
export async function createSpurtMeeting(a: SpurtMeetingArgs, calendar?: { calendarId: string; location: string; description: string }): Promise<SpurtMeetingLink & { onCalendar: boolean }> {
  const m = await createMeeting(bodyFor(a));
  const url = m.joinUrl ?? (await meetingLink(m.id)).joinUrl;
  const link = { id: m.id, code: m.code ?? codeFromUrl(url), url };
  const push = await calendarPush();
  if (push === "active" || !calendar) return { ...link, onCalendar: push === "active" };
  try {
    const day = new Date(a.startAt);
    const ymd = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
    const t = (iso: string) => {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    };
    const base = {
      calendarId: calendar.calendarId,
      title: a.title,
      description: `${calendar.description}${calendar.description ? "\n\n" : ""}Join Spurt meeting: ${url}`,
      date: ymd,
      start: t(a.startAt),
      end: t(a.endAt),
      guests: a.guests,
      onlineProvider: SPURT_PROVIDER,
      onlineUrl: url,
      ...(a.repeat !== "none" && a.repeat !== "custom" ? { recurrence: a.repeat } : {}),
    };
    try {
      await createEvent({ ...base, location: calendar.location || url });
    } catch (err) {
      // the calendar service may not know "Spurt" yet: keep the link in the location and description instead
      if (!(err instanceof CalendarError) || err.status !== 400) throw err;
      await createEvent({ ...base, onlineProvider: "", onlineUrl: "", location: calendar.location || url });
    }
    return { ...link, onCalendar: true };
  } catch (err) {
    await cancelMeeting(m.id, a.repeat !== "none" ? "series" : "single").catch(() => undefined); // no orphan meeting
    throw err;
  }
}

const findByCode = async (code: string, aroundMs: number): Promise<MeetingView | undefined> => {
  const from = aroundMs - 24 * 3600_000;
  const items = await listMeetings(from, from + 365 * 24 * 3600_000); // the server allows at most 366 days
  return items.find((m) => (m.code && cleanCode(m.code) === code) || (m.joinUrl && codeFromUrl(m.joinUrl) === code));
};

/** Change a meeting you made from a calendar event (found by the code in its join link). */
export async function updateSpurtMeeting(url: string, a: SpurtMeetingArgs): Promise<void> {
  const code = codeFromUrl(url);
  const m = code ? await findByCode(code, Date.parse(a.startAt)) : undefined;
  if (!m) throw new ApiError(404, "not_found", "That meeting could not be found. It may have been cancelled.");
  const b = bodyFor(a);
  const patch: Record<string, unknown> = { title: b.title, description: b.description ?? "", startMs: b.startMs, endMs: b.endMs, timeZone: b.timeZone, agenda: b.agenda ?? [] };
  await patchMeeting(m.seriesId || m.id, patch, m.seriesId || m.recurring ? "series" : "single");
}

/** Cancel the meeting behind a calendar event. A meeting that is already gone is fine. */
export async function removeSpurtMeeting(url: string): Promise<void> {
  const code = codeFromUrl(url);
  if (!code) return;
  const m = await findByCode(code, Date.now());
  if (!m) return;
  await cancelMeeting(m.seriesId || m.id, m.seriesId || m.recurring ? "series" : "single");
}

/** Accept, say maybe, or decline a Spurt meeting from its calendar event (found by the code in its join link). */
export async function respondToMeeting(url: string, atMs: number, answer: "accepted" | "tentative" | "declined"): Promise<void> {
  const code = codeFromUrl(url);
  const m = code ? await findByCode(code, atMs) : undefined;
  if (!m) throw new ApiError(404, "not_found", "That meeting could not be found. It may have been cancelled.");
  await rsvp(m.id, answer);
}